/**
 * LyX-style editing commands on top of ProseMirror.
 */
import { type Command, type EditorState, TextSelection, NodeSelection, Selection, type Transaction } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';
import { type Node as PMNode, type MarkType, type Mark, Fragment, type Attrs, type ResolvedPos } from 'prosemirror-model';
import { splitBlock } from 'prosemirror-commands';
import type { EditorView } from 'prosemirror-view';
import { schema, commentHeader, formatTimestamp, unquote, paramMap, FONT_KEYS } from '@overlyx/core';
import { nextLayout, isHeadingLayout, isListLayout, isEnvironmentLayout } from './layouts';
import { editorContext } from './context';
import { recordInteraction } from '../usage';
import { MathInlineView, MathDisplayView, pendingFocus } from './nodeviews/math';
import { setValueMarkIn } from './fontsize';
import { isLayoutDoc, pageAt } from './layout/commands';
import { placeImage } from './layout/images';

/* ------------------------------------------------------------ paragraphs */

/** The paragraph containing the selection head (may be inside an inset). */
export function currentParagraph(state: EditorState): { node: PMNode; pos: number; depth: number } | null {
  const $from = state.selection.$from;
  for (let d = $from.depth; d >= 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'paragraph') return { node: n, pos: $from.before(d), depth: d };
  }
  return null;
}

export function inInset(state: EditorState): boolean {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const t = $from.node(d).type.name;
    if (t === 'inset' || t === 'table_cell') return true;
  }
  return false;
}

/** Apply a layout to all paragraphs touched by the selection. */
/**
 * The paragraphs of the selection at the cursor's own level (LyX): inside a footnote, note, float
 * or table cell the inset's paragraphs are the ones a layout / depth / alignment change applies
 * to, not the paragraph around the inset (which nodesBetween reaches first).
 */
function forParagraphsAtCursorLevel(state: EditorState, fn: (node: PMNode, pos: number) => void): boolean {
  const { from, to, $from } = state.selection;
  let level = $from.depth;
  while (level > 0 && $from.node(level).type.name !== 'paragraph') level--;
  let any = false;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'paragraph' && state.doc.resolve(pos).depth === level - 1) { fn(node, pos); any = true; return false; }
    return true;
  });
  return any;
}

/** A table, float or display formula: what a heading's LaTeX argument cannot hold. */
function isBlockAtom(node: PMNode): boolean {
  const t = node.type.name;
  return t === 'table' || t === 'math_display' || t === 'macro' || (t === 'inset' && (node.attrs.name === 'Float' || node.attrs.name === 'Wrap'));
}

export function setLayout(layout: string): Command {
  return (state, dispatch) => {
    const tr = state.tr;
    // a heading style with the cursor after a table (a float, a display formula) in its paragraph
    // — the table that ends the document, Ctrl+End, Section — starts a paragraph of its own after
    // it: a heading cannot hold a table, and one paragraph of both lost the table to a deletion
    const { $from, empty } = state.selection;
    if (empty && $from.parent.type.name === 'paragraph' && isHeadingLayout(layout, editorContext.meta?.layouts)) {
      let after = -1;
      $from.parent.forEach((child, offset) => { if (offset < $from.parentOffset && isBlockAtom(child)) after = offset + child.nodeSize; });
      if (after >= 0) {
        if (dispatch) dispatch(tr.split($from.start() + after, 1, [{ type: $from.parent.type, attrs: { ...$from.parent.attrs, layout } }]).scrollIntoView());
        return true;
      }
    }
    const any = forParagraphsAtCursorLevel(state, (node, pos) => { if (node.attrs.layout !== layout) tr.setNodeMarkup(pos, undefined, { ...node.attrs, layout }); });
    if (!any) return false;
    if (dispatch) dispatch(tr.scrollIntoView());
    return true;
  };
}

/**
 * setLayout for a layout the document class may not have (LyX: "Layout `Chapter' not known" does
 * nothing): an article has no Chapter, revtex no Subparagraph. The key is still consumed — it was
 * meant for the editor, the browser must not get it — and the status bar says why nothing happened.
 */
export function setKnownLayout(layout: string): Command {
  return (state, dispatch, view) => {
    const layouts = editorContext.meta?.layouts;
    if (layouts && layouts.length && !layouts.some(l => l.name === layout)) {
      if (dispatch) editorContext.notify?.(`This document class has no “${layout}” paragraph style`);
      return true;
    }
    return setLayout(layout)(state, dispatch, view);
  };
}

export function setParagraphAttrs(attrs: Partial<Record<string, unknown>>): Command {
  return (state, dispatch) => {
    const tr = state.tr;
    const any = forParagraphsAtCursorLevel(state, (node, pos) => { tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs }); });
    if (!any) return false;
    if (dispatch) dispatch(tr);
    return true;
  };
}

/** LyX Paragraph::getMaxDepthAfter: how deep the paragraph after `prev` may nest — one step deeper under an environment (a list item), else as deep as `prev`. */
function maxDepthAfter(prev: PMNode | null | undefined, prevDepth = (prev?.attrs.depth as number) || 0): number {
  if (prev?.type.name !== 'paragraph') return 0;
  return isEnvironmentLayout(prev.attrs.layout as string, editorContext.meta?.layouts) ? prevDepth + 1 : prevDepth;
}

/** the paragraph before the cursor's paragraph in the same container (the document, an inset, a cell), if any */
function paragraphBefore(state: EditorState): PMNode | null {
  const cur = currentParagraph(state);
  if (!cur) return null;
  const $p = state.doc.resolve(cur.pos);
  return $p.index() > 0 ? $p.parent.child($p.index() - 1) : null;
}

/**
 * LyX Text::changeDepth: a paragraph may nest one step deeper only under its predecessor
 * (getMaxDepthAfter: prev.depth + 1 when prev's layout is an environment, else prev.depth);
 * unnesting just needs depth > 0. The max depth runs forward through the selection, so a
 * paragraph indented here lets the next selected one follow.
 */
export function changeDepth(delta: 1 | -1): Command {
  return (state, dispatch) => {
    const { from, to, $from } = state.selection;
    let level = $from.depth;
    while (level > 0 && $from.node(level).type.name !== 'paragraph') level--;
    const tr = state.tr;
    let any = false;
    const newDepths = new Map<PMNode, number>();
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name !== 'paragraph') return true;
      const $pos = state.doc.resolve(pos);
      if ($pos.depth !== level - 1) return true;
      const prev = $pos.index() > 0 ? $pos.parent.child($pos.index() - 1) : null;
      const maxDepth = prev ? maxDepthAfter(prev, newDepths.get(prev) ?? ((prev.attrs.depth as number) || 0)) : 0;
      const depth = (node.attrs.depth as number) || 0;
      let next = depth;
      if (delta > 0 && depth < maxDepth) next = depth + 1;
      else if (delta < 0 && depth > 0) next = depth - 1;
      newDepths.set(node, next);
      if (next !== depth) { tr.setNodeMarkup(pos, undefined, { ...node.attrs, depth: next }); any = true; }
      return false;
    });
    if (!any) return false;
    if (dispatch) dispatch(tr);
    return true;
  };
}

/**
 * Tab / Shift+Tab as list indent (LyX site.bind: Tab falls through cell-forward to
 * depth-increment). Claimed in list paragraphs, nested continuation paragraphs, and a paragraph
 * that can nest under the list item above it (Tab puts a paragraph or an image after a list into
 * that item), so the key keeps its browser meaning in ordinary text — but once claimed the key is
 * consumed even if no depth change is possible, so focus never tabs out of the editor mid-list.
 */
export function listIndent(delta: 1 | -1): Command {
  return (state, dispatch) => {
    const cur = currentParagraph(state);
    if (!cur) return false;
    const depth = (cur.node.attrs.depth as number) || 0;
    const underItem = delta > 0 && maxDepthAfter(paragraphBefore(state)) > depth;
    if (!isListLayout(cur.node.attrs.layout as string) && !(depth > 0) && !underItem) return false;
    changeDepth(delta)(state, dispatch);
    return true;
  };
}

/** Document ▸ Start Appendix Here (LyX: \start_of_appendix): the cursor's paragraph begins the appendix — \appendix is written before it, headings from here on are lettered. */
export const toggleAppendix: Command = (state, dispatch) => {
  const cur = currentParagraph(state);
  if (!cur) return false;
  dispatch?.(state.tr.setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, appendix: !cur.node.attrs.appendix }));
  return true;
};

/**
 * Insets that hold one paragraph (LyX: InsetLayout MultiPar false — a paragraph break in
 * `\caption{…}` or an optional argument would end the LaTeX argument and break the build).
 */
const SINGLE_PARAGRAPH_INSETS: Record<string, string> = { Caption: 'A caption', Argument: 'An argument', Index: 'An index entry' };

/** The single-paragraph inset the cursor is in (its description), if the innermost text container is one. */
export function singleParagraphInset(state: EditorState): string | null {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'table_cell' || n.type.name === 'table_header') return null;
    if (n.type.name === 'inset') return SINGLE_PARAGRAPH_INSETS[n.attrs.name as string] ?? null;
  }
  return null;
}

/** LyX disables the paragraph break in a single-paragraph inset: the key does nothing (the status bar says why). */
function refuseParagraphBreak(state: EditorState, dispatch: unknown): boolean {
  const what = singleParagraphInset(state);
  if (!what) return false;
  if (dispatch) editorContext.notify?.(`${what} is a single paragraph — Enter does not split it`);
  return true;
}

/** Enter: split paragraph; the new paragraph gets LyX's "next layout". */
export const paragraphBreak: Command = (state, dispatch, view) => {
  if (refuseParagraphBreak(state, dispatch)) return true;
  const cur = currentParagraph(state);
  if (!cur) return false;
  const { $from } = state.selection;
  const atEnd = $from.parentOffset === $from.parent.content.size;
  const inset = inInset(state);
  const layouts = editorContext.meta?.layouts;
  const next = nextLayout(cur.node.attrs.layout, inset, layouts);
  // Enter on an empty list item ends the list (Google Docs): the item becomes an ordinary paragraph
  if (state.selection.empty && cur.node.content.size === 0 && $from.parent === cur.node && isListLayout(cur.node.attrs.layout as string)) return leaveList(state, dispatch, view);
  if (!dispatch) return true;
  let tr = state.tr.deleteSelection();
  const pos = tr.selection.from;
  const $pos = tr.doc.resolve(pos);
  // paragraphs are always splittable in our schema (block+ / paragraph+)
  // the new paragraph: the layout LyX gives it; never the start-of-appendix marker (it belongs to one paragraph)
  const attrs = { ...cur.node.attrs, layout: atEnd ? next : cur.node.attrs.layout, endChange: null, appendix: false };
  tr = tr.split(pos, 1, [{ type: schema.nodes.paragraph, attrs }]);
  if (!atEnd && isHeadingLayout(cur.node.attrs.layout, layouts)) {
    // splitting in the middle of a heading keeps the layout (LyX behaviour)
  }
  // paragraph break inside a change-tracked document
  if (editorContext.trackChanges && editorContext.changeAuthorId !== undefined) {
    const before = tr.doc.resolve(tr.selection.from).before();
    const prev = tr.doc.resolve(before - 1).nodeBefore;
    if (prev && prev.type.name === 'paragraph') {
      tr = tr.setNodeMarkup(before - prev.nodeSize, undefined, { ...prev.attrs, endChange: JSON.stringify({ type: 'inserted', author: editorContext.changeAuthorId, time: Math.floor(Date.now() / 1000) }) });
    }
  }
  void $pos;
  dispatch(tr.scrollIntoView());
  return true;
};

/**
 * Out of the list, Google-Docs style: a nested item (or any nested paragraph) moves out one level
 * first; an item at the top level becomes an ordinary paragraph, its text kept. Bound to Backspace
 * at the start of an item (`listExitBackspace`) and to Enter on an empty one (`paragraphBreak`).
 */
const leaveList: Command = (state, dispatch) => {
  const cur = currentParagraph(state);
  if (!cur) return false;
  const depth = (cur.node.attrs.depth as number) || 0;
  if (!dispatch) return true;
  if (depth > 0) dispatch(state.tr.setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, depth: depth - 1 }).scrollIntoView());
  else dispatch(state.tr.setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, layout: inInset(state) ? 'Plain Layout' : 'Standard' }).scrollIntoView());
  return true;
};

/**
 * Backspace at the start of a list item takes the bullet away and keeps the text where it was
 * (Google Docs): it becomes a paragraph of the item above — text, an image, a formula belonging to
 * that item (LaTeX: a paragraph inside its \item; markdown: an indented block under it) — as deep as
 * LyX lets it nest there (the first item of a list has no item above: out of the list, or into the
 * parent item). The next Backspace moves such a nested paragraph out one level, and so on, instead
 * of joining the paragraphs.
 */
export const listExitBackspace: Command = (state, dispatch, view) => {
  const { $from, empty } = state.selection;
  if (!empty || $from.parentOffset !== 0) return false;
  const par = $from.parent;
  if (par.type.name !== 'paragraph') return false;
  const depth = (par.attrs.depth as number) || 0;
  if (isListLayout(par.attrs.layout as string)) {
    if (!dispatch) return true;
    const nest = Math.min(depth + 1, maxDepthAfter(paragraphBefore(state)));
    dispatch(state.tr.setNodeMarkup($from.before(), undefined, { ...par.attrs, layout: inInset(state) ? 'Plain Layout' : 'Standard', depth: nest }).scrollIntoView());
    return true;
  }
  if (depth === 0) return false;
  return leaveList(state, dispatch, view);
};

/** Alt+Enter — "paragraph-break inverse": new paragraph with the default layout (or keep). */
export const paragraphBreakInverse: Command = (state, dispatch) => {
  if (refuseParagraphBreak(state, dispatch)) return true;
  const cur = currentParagraph(state);
  if (!cur) return false;
  const inset = inInset(state);
  const def = inset ? 'Plain Layout' : 'Standard';
  const layout = cur.node.attrs.layout === def ? def : def;
  if (!dispatch) return true;
  let tr = state.tr.deleteSelection();
  tr = tr.split(tr.selection.from, 1, [{ type: schema.nodes.paragraph, attrs: { ...cur.node.attrs, layout, endChange: null, appendix: false } }]);
  dispatch(tr.scrollIntoView());
  return true;
};

/* ----------------------------------------------------------------- fonts */

/** LyX font toggles are value marks: emph on/off, series bold/medium ... */
export function toggleValueMark(markName: string, onValue: string, offValue?: string): Command {
  return (state, dispatch) => {
    const type: MarkType = schema.marks[markName];
    const { from, to, empty, $cursor } = state.selection as TextSelection;
    const has = (marks: readonly { type: MarkType; attrs: Attrs }[]) => marks.find(m => m.type === type);
    if (empty && $cursor) {
      const marks = state.storedMarks ?? $cursor.marks();
      const cur = has(marks);
      const on = cur?.attrs.value === onValue;
      if (!dispatch) return true;
      if (on) dispatch(state.tr.setStoredMarks(marks.filter(m => m.type !== type).concat(offValue ? [type.create({ value: offValue })] : [])));
      else dispatch(state.tr.setStoredMarks(marks.filter(m => m.type !== type).concat([type.create({ value: onValue })])));
      return true;
    }
    // range: if every text node has the mark "on" → remove; else set
    let allOn = true, anyText = false;
    state.doc.nodesBetween(from, to, (node) => {
      if (node.isText) { anyText = true; const m = has(node.marks); if (!(m && m.attrs.value === onValue)) allOn = false; }
    });
    if (!anyText) return false;
    if (!dispatch) return true;
    let tr = state.tr;
    if (allOn) { tr = tr.removeMark(from, to, type); if (offValue) tr = tr.addMark(from, to, type.create({ value: offValue })); }
    else { tr = tr.removeMark(from, to, type).addMark(from, to, type.create({ value: onValue })); }
    dispatch(tr);
    return true;
  };
}

export function setValueMark(markName: string, value: string | null): Command {
  return (state, dispatch) => {
    const type: MarkType = schema.marks[markName];
    const { empty, $cursor } = state.selection as TextSelection;
    if (!dispatch) return true;
    if (empty && $cursor) {
      const marks = (state.storedMarks ?? $cursor.marks()).filter(m => m.type !== type);
      dispatch(state.tr.setStoredMarks(value ? marks.concat([type.create({ value })]) : marks));
      return true;
    }
    // every range of the selection (table cells), and inline nodes in it — formulas, insets — through
    // their `marks` attr (inline nodes keep their fonts there: schema.ts)
    let tr = state.tr;
    for (const r of state.selection.ranges) tr = setValueMarkIn(tr, r.$from.pos, r.$to.pos, markName, value);
    dispatch(tr);
    return true;
  };
}

/**
 * A font by name on the selection (core fontface.ts: `Family|axes|features`), or none (null). It
 * takes the place of a serif / sans / monospace family: the two do not stack (\textsf inside
 * \olfont would win over it).
 */
export function setFontFace(value: string | null): Command {
  return (state, dispatch) => {
    if (!dispatch) return true;
    const face = schema.marks.face, family = schema.marks.family;
    const { empty, $cursor } = state.selection as TextSelection;
    if (empty && $cursor) {
      const marks = (state.storedMarks ?? $cursor.marks()).filter(m => m.type !== face && (!value || m.type !== family));
      dispatch(state.tr.setStoredMarks(value ? marks.concat([face.create({ value })]) : marks));
      return true;
    }
    let tr = state.tr;
    for (const r of state.selection.ranges) {
      if (value) tr = setValueMarkIn(tr, r.$from.pos, r.$to.pos, 'family', null);
      tr = setValueMarkIn(tr, r.$from.pos, r.$to.pos, 'face', value);
    }
    dispatch(tr);
    return true;
  };
}

/** A LaTeX family (roman / sans / typewriter; null: the paragraph's) on the selection, in place of a font by name. */
export function setFontFamily(value: string | null): Command {
  return (state, dispatch) => {
    if (!dispatch) return true;
    let st = state, tr = state.tr;
    setFontFace(null)(st, t => { tr = t; });
    st = state.apply(tr);
    setValueMark('family', value)(st, t => { t.steps.forEach(step => tr.step(step)); if (t.storedMarksSet) tr.setStoredMarks(t.storedMarks); });
    dispatch(tr);
    return true;
  };
}

export const fontCommands = {
  emph: toggleValueMark('emph', 'on', undefined),
  bold: toggleValueMark('series', 'bold', undefined),
  noun: toggleValueMark('noun', 'on'),
  underline: toggleValueMark('bar', 'under'),
  strikeout: toggleValueMark('strikeout', 'on'),
  typewriter: toggleValueMark('family', 'typewriter'),
  sans: toggleValueMark('family', 'sans'),
  italic: toggleValueMark('shape', 'italic'),
  smallcaps: toggleValueMark('shape', 'smallcaps'),
  slanted: toggleValueMark('shape', 'slanted'),
  uuline: toggleValueMark('uuline', 'on'),
  uwave: toggleValueMark('uwave', 'on'),
  xout: toggleValueMark('xout', 'on'),
};

/** Reset all font attributes on the selection (LyX "font-default"). */
export const fontDefault: Command = (state, dispatch) => {
  const { from, to, empty } = state.selection;
  if (!dispatch) return true;
  if (empty) { dispatch(state.tr.setStoredMarks([])); return true; }
  let tr = state.tr;
  for (const name of ['family', 'face', 'series', 'shape', 'size', 'emph', 'bar', 'strikeout', 'xout', 'uuline', 'uwave', 'noun', 'color', 'numeric']) tr = tr.removeMark(from, to, schema.marks[name]);
  dispatch(tr);
  return true;
};

/* ---------------------------------------------------------------- insets */

const isFontMark = (name: string) => (FONT_KEYS as readonly string[]).includes(name);

/**
 * The font in effect at the cursor (the stored marks after a font toggle, else the surrounding text's), as
 * entries of an inline node's `marks` attr. LyX: an inset or formula inserted into emphasized text is
 * emphasized too (`\emph{For $G$ fixed}`), instead of splitting the run around it.
 */
export function inheritedFontMarks(state: EditorState): { type: string; attrs: Attrs }[] {
  const marks = state.storedMarks ?? state.selection.$from.marks();
  return marks.filter(m => isFontMark(m.type.name)).map(m => ({ type: m.type.name, attrs: m.attrs }));
}

/** The font marks an inline node carries in its `marks` attr, as ProseMirror marks (typing after it continues in that font). */
export function fontMarksOf(node: PMNode): Mark[] {
  if (node.isText || !node.attrs.marks) return [];
  try {
    const list = JSON.parse(node.attrs.marks) as { type: string; attrs?: Attrs }[];
    return list.filter(m => isFontMark(m.type) && schema.marks[m.type]).map(m => schema.marks[m.type].create(m.attrs));
  } catch { return []; }
}

/** `node` with the cursor's font marks added to its `marks` attr (nodes without that attr are returned as they are). */
function withInheritedFont(state: EditorState, node: PMNode): PMNode {
  if (node.isText || !node.type.spec.attrs || !('marks' in node.type.spec.attrs)) return node;
  const inherited = inheritedFontMarks(state);
  if (!inherited.length) return node;
  let existing: unknown[] = [];
  try { existing = node.attrs.marks ? JSON.parse(node.attrs.marks) : []; } catch { existing = []; }
  return node.type.create({ ...node.attrs, marks: JSON.stringify([...inherited, ...existing]) }, node.content, node.marks);
}

/**
 * Where something inserted goes when an object is selected: a selected image, formula, table or inset
 * (ProseMirror selects it as a node when it is clicked) or a block of table cells is never replaced by
 * it — the insertion goes right after the object, at the end of the last selected cell. Selected text
 * is replaced, as typing replaces it.
 */
export function keepSelectedObjects<T extends Transaction>(tr: T): T {
  const sel = tr.selection;
  if (sel instanceof NodeSelection && sel.node.isInline) tr.setSelection(TextSelection.create(tr.doc, sel.to));
  else if (sel instanceof CellSelection) tr.setSelection(Selection.near(tr.doc.resolve(sel.ranges[sel.ranges.length - 1].$to.pos), -1));
  return tr;
}

/**
 * On a layout page outside its text boxes (a gap cursor on the canvas, an object selected) an inline
 * node has no paragraph to go into, and ProseMirror would make one up — in the page's speaker notes
 * (`\note{}`), on a new page. Images become image objects there (insertGraphics); everything else
 * asks for a text box first.
 */
function outsideLayoutText(state: EditorState, dispatch: unknown): boolean {
  if (!isLayoutDoc(state.doc) || state.selection.$from.parent.inlineContent) return false;
  if (dispatch) editorContext.notify?.('Click into a text box first (double-click the page to start a new one)');
  return true;
}

function insertInline(state: EditorState, node: PMNode): Transaction {
  const tr = keepSelectedObjects(state.tr).replaceSelectionWith(withInheritedFont(state, node), false);
  return tr;
}

export function insertNode(node: PMNode, selectInside = false): Command {
  return (state, dispatch) => {
    if (outsideLayoutText(state, dispatch)) return false;
    if (!dispatch) return true;
    const tr = insertInline(state, node);
    if (selectInside) {
      const pos = tr.selection.from - node.nodeSize;
      tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 2)));
    }
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/**
 * What an inset inserted around the selection takes in — LyX moves the selection into the new inset
 * (doInsertInset): the selected text or object of one paragraph as one paragraph, or the selected
 * paragraphs (cut where the selection starts and ends). null when there is nothing to move, or when the
 * selection cannot move in one piece (table cells, a range from inside an inset to outside it): the
 * inset then goes after the selection, and nothing is removed.
 */
function selectedParagraphs(state: EditorState): PMNode[] | null {
  const sel = state.selection;
  if (sel.empty || sel instanceof CellSelection) return null;
  const { $from, $to, from, to } = sel;
  if ($from.sameParent($to) && $from.parent.inlineContent) return [schema.nodes.paragraph.create({ layout: 'Plain Layout' }, state.doc.slice(from, to).content)];
  if ($from.depth !== $to.depth || $from.parent.type !== schema.nodes.paragraph || $to.parent.type !== schema.nodes.paragraph || $from.node($from.depth - 1) !== $to.node($to.depth - 1)) return null;
  const paras: PMNode[] = [];
  state.doc.slice(from, to).content.forEach(p => paras.push(p.type.create({ ...p.attrs, endChange: null, appendix: false }, p.content)));
  // a selection from the end of a paragraph, or to the start of one, does not take that paragraph along
  if ($from.parentOffset === $from.parent.content.size && paras.length > 1 && !paras[0].content.size) paras.shift();
  if ($to.parentOffset === 0 && paras.length > 1 && !paras[paras.length - 1].content.size) paras.pop();
  return paras;
}

/** The change mark (in an inline node's `marks` attr) of something inserted while changes are tracked. */
function insertedMarks(): string | undefined {
  if (!editorContext.trackChanges || editorContext.changeAuthorId === undefined) return undefined;
  return JSON.stringify([{ type: 'change', attrs: { type: 'inserted', author: editorContext.changeAuthorId, time: Math.floor(Date.now() / 1000) } }]);
}

/** Put the inset `node` in place of the selection of `tr`; the cursor goes to the end of its first paragraph. */
function placeInset(tr: Transaction, node: PMNode): Transaction {
  tr.replaceSelectionWith(node, false);
  const pos = tr.selection.from - node.nodeSize;
  const inner = pos + 1 + 1 + (node.firstChild?.content.size ?? 0);
  return tr.setSelection(TextSelection.create(tr.doc, Math.min(inner, tr.doc.content.size)));
}

/**
 * Insert a text-containing inset around the selection (LyX: the selection moves into it; see
 * selectedParagraphs), or an empty one, cursor inside. A selection that cannot move in one piece
 * stays as it is and the inset goes after it.
 */
export function insertTextInset(name: string, arg = '', params: string[] = [], status: 'open' | 'collapsed' | null = 'open'): Command {
  return (state, dispatch) => {
    if (outsideLayoutText(state, dispatch)) return false;
    const taken = selectedParagraphs(state);
    const paras = taken ?? [schema.nodes.paragraph.create({ layout: 'Plain Layout' })];
    const attrs: Record<string, unknown> = { name, arg, params: JSON.stringify(params), status };
    const marks = insertedMarks();
    if (marks) attrs.marks = marks;
    const node = schema.nodes.inset.create(attrs, Fragment.from(paras));
    if (!dispatch) return true;
    let tr = state.tr;
    if (!taken && !state.selection.empty) tr.setSelection(Selection.near(tr.doc.resolve(state.selection.to), -1));
    tr = placeInset(tr, node);
    dispatch(tr.scrollIntoView());
    return true;
  };
}

export const insertNote = (kind: 'Note' | 'Comment' | 'Greyedout') => insertTextInset('Note', kind, [], 'open');
export const insertFootnote = insertTextInset('Foot', '', [], 'open');
export const insertMarginal = insertTextInset('Marginal', '', [], 'open');
export const insertERT = insertTextInset('ERT', '', [], 'open');
export const insertBox = insertTextInset('Box', 'Frameless', ['position "t"', 'hor_pos "c"', 'has_inner_box 1', 'inner_pos "t"', 'use_parbox 0', 'use_makebox 0', 'width "100col%"', 'special "none"', 'height "1in"', 'height_special "totalheight"', 'thickness "0.4pt"', 'separation "3pt"', 'shadowsize "4pt"', 'framecolor "black"', 'backgroundcolor "none"'], 'open');
export const insertBranch = (name: string) => insertTextInset('Branch', name, ['inverted 0'], 'open');
export const insertFlex = (name: string) => insertTextInset('Flex', name, [], 'collapsed');
export const insertArgument = (n: string) => insertTextInset('Argument', n, [], 'open');
export const insertIndex = insertTextInset('Index', 'idx', ['range none', 'pageformat default'], 'collapsed');
export const insertListing = insertTextInset('listings', '', ['lstparams ""', 'inline false'], 'open');

/** The float inset around `pos`, if any (its node and position). */
function enclosingFloat($pos: ResolvedPos): { node: PMNode; pos: number; depth: number } | null {
  for (let d = $pos.depth; d > 0; d--) {
    const n = $pos.node(d);
    if (n.type === schema.nodes.inset && (n.attrs.name === 'Float' || n.attrs.name === 'Wrap')) return { node: n, pos: $pos.before(d), depth: d };
  }
  return null;
}

const emptyCaption = () => schema.nodes.inset.create({ name: 'Caption', arg: 'Standard', params: '[]', status: null }, schema.nodes.paragraph.create({ layout: 'Plain Layout' }));

/**
 * The caret (or a text selection) in a table's cell: the state with the table selected instead — a
 * caption or a float made there is the table's, not a caption or a float inside the cell (which wrote
 * raw \caption into the table).
 */
function tableSelected(state: EditorState): EditorState | null {
  if (state.selection instanceof NodeSelection) return null;
  const $f = state.selection.$from;
  for (let d = $f.depth; d > 0; d--) if ($f.node(d).type === schema.nodes.table) return state.apply(state.tr.setSelection(NodeSelection.create(state.doc, $f.before(d))));
  return null;
}

/**
 * Insert ▸ Caption. Selected text becomes the caption (LyX moves it in). With an image or a table
 * selected the caption is for it: outside a float the object is put into a new figure / table float
 * with an empty caption; inside one, the caption gets a paragraph of its own beside the object's —
 * below a figure, above a table, as insertFloat lays them out. A caption never replaces what is selected.
 */
export const insertCaption: Command = (state, dispatch, view) => {
  const inTable = tableSelected(state);
  if (inTable) return insertCaption(inTable, dispatch, view);
  const sel = state.selection;
  if (sel instanceof NodeSelection && sel.node.isInline && !sel.node.isText) {
    const float = enclosingFloat(sel.$from);
    if (!float) return insertFloat(sel.node.type === schema.nodes.table ? 'table' : 'figure')(state, dispatch, view);
    if (!dispatch) return true;
    // the object's paragraph inside the float, and the caption's beside it
    const parDepth = sel.$from.depth;
    const above = float.node.attrs.arg === 'table';
    const at = above ? sel.$from.before(parDepth) : sel.$from.after(parDepth);
    const tr = state.tr.insert(at, schema.nodes.paragraph.create({ layout: 'Plain Layout' }, emptyCaption()));
    dispatch(tr.setSelection(TextSelection.create(tr.doc, at + 3)).scrollIntoView());
    return true;
  }
  return insertTextInset('Caption', 'Standard', [], null)(state, dispatch, view);
};

/**
 * OverLyX comment thread: a Note Comment inset with an author/time header paragraph, the cursor in
 * the empty message paragraph below it. A thread is a point in the text (`%% @comment` in the
 * file has no range): commenting on a selection keeps the selected text and anchors the thread
 * right after it, as the margin cards show.
 */
export const insertComment: Command = (state, dispatch) => {
  if (outsideLayoutText(state, dispatch)) return false;
  const user = editorContext.user?.name ?? 'Anonymous';
  const header = schema.nodes.paragraph.create({ layout: 'Plain Layout' }, schema.text(commentHeader(user, formatTimestamp())));
  const body = schema.nodes.paragraph.create({ layout: 'Plain Layout' });
  // never a suggestion, also while suggesting (plugins/changes.ts isComment)
  const attrs: Record<string, unknown> = { name: 'Note', arg: 'Comment', params: '[]', status: 'open' };
  const node = schema.nodes.inset.create(attrs, Fragment.from([header, body]));
  if (!dispatch) return true;
  const tr = state.tr.setSelection(Selection.near(state.doc.resolve(commentAnchor(state)), -1));
  placeInset(tr, node);
  // the cursor sits at the end of the header paragraph: +1 closes it, +1 opens the body paragraph
  const pos = tr.selection.from + 2;
  dispatch(tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(pos, tr.doc.content.size)))).scrollIntoView());
  return true;
};

/** Where a comment on the selection is anchored: the end of the selection — of its last paragraph when it ends at the start of the next one. */
function commentAnchor(state: EditorState): number {
  const sel = state.selection;
  if (sel.empty) return sel.from;
  const last = sel.ranges[sel.ranges.length - 1];
  let $to = last.$to;
  if ($to.parent.inlineContent && $to.parentOffset === 0 && $to.pos > sel.from) {
    const before = Selection.findFrom(state.doc.resolve($to.before()), -1, true);
    if (before && before.from >= sel.from) $to = before.$to;
  }
  return $to.pos;
}

/**
 * Insert ▸ Float: a figure / table / algorithm float with a caption. LyX moves the selection into it
 * (a selected image, table, text or paragraphs becomes its content) and puts the cursor into the
 * caption; an empty float gets an empty content paragraph, the cursor there.
 */
export function insertFloat(type: 'figure' | 'table' | 'algorithm' = 'figure'): Command {
  return (state, dispatch) => {
    const inTable = type !== 'algorithm' ? tableSelected(state) : null;
    if (inTable) return insertFloat(type)(inTable, dispatch);
    if (outsideLayoutText(state, dispatch)) return false;
    const taken = selectedParagraphs(state);
    // figures and tables are centered (the usual layout); an algorithm's steps are ordinary left-aligned paragraphs
    const centred = (p?: PMNode) => schema.nodes.paragraph.create(type === 'algorithm' ? { ...p?.attrs, layout: 'Plain Layout' } : { ...p?.attrs, layout: 'Plain Layout', align: 'center' }, p?.content);
    const body = taken ? (taken.length === 1 ? [centred(taken[0])] : taken) : [centred()];
    const captionPar = schema.nodes.paragraph.create({ layout: 'Plain Layout' }, emptyCaption());
    const content = type === 'table' ? [captionPar, ...body] : [...body, captionPar];
    const float = schema.nodes.inset.create({ name: 'Float', arg: type, params: JSON.stringify(['placement document', 'alignment document', 'wide false', 'sideways false']), status: 'open' }, Fragment.from(content));
    if (!dispatch) return true;
    let tr = state.tr;
    if (!taken && !state.selection.empty) tr.setSelection(Selection.near(tr.doc.resolve(state.selection.to), -1));
    tr = tr.replaceSelectionWith(float, false);
    const pos = tr.selection.from - float.nodeSize;
    // into the caption when something was moved in (its paragraph's start: +1 float, +1 paragraph, +1 caption, +1 its paragraph)
    let caret = pos + 2;
    if (taken) {
      let off = pos + 1;
      for (let i = 0; i < content.length - 1 && content[i] !== captionPar; i++) off += content[i].nodeSize;
      caret = off + 3;
    }
    tr = tr.setSelection(TextSelection.create(tr.doc, caret));
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** Graphics inset parameters (LyX InsetGraphicsParams). Flags are written as bare words. */
export interface GraphicsOpts {
  lyxscale?: string; draft?: boolean; scale?: string; width?: string; height?: string; keepAspectRatio?: boolean;
  rotateAngle?: string; rotateOrigin?: string; scaleBeforeRotation?: boolean; BoundingBox?: string; clip?: boolean; special?: string; groupId?: string;
}

/** Parameter lines in the order LyX writes them (InsetGraphicsParams::Write). */
export function graphicsParams(filename: string, o: GraphicsOpts = {}): string[] {
  const params = [`\tfilename ${filename}`];
  if (o.lyxscale) params.push(`\tlyxscale ${o.lyxscale}`);
  if (o.draft) params.push('\tdraft');
  if (o.scale) params.push(`\tscale ${o.scale}`);
  if (o.width) params.push(`\twidth ${o.width}`);
  if (o.height) params.push(`\theight ${o.height}`);
  if (o.keepAspectRatio) params.push('\tkeepAspectRatio');
  if (o.rotateAngle && o.rotateAngle !== '0') params.push(`\trotateAngle ${o.rotateAngle}`);
  if (o.rotateOrigin && o.rotateOrigin !== 'center') params.push(`\trotateOrigin ${o.rotateOrigin}`);
  if (o.scaleBeforeRotation) params.push('\tscaleBeforeRotation');
  if (o.BoundingBox) params.push(`\tBoundingBox ${o.BoundingBox}`);
  if (o.clip) params.push('\tclip');
  if (o.special) params.push(`\tspecial ${o.special}`);
  if (o.groupId) params.push(`\tgroupId ${o.groupId}`);
  params.push('');
  return params;
}

/** The inverse of graphicsParams for an existing inset. */
export function graphicsOpts(params: string[]): GraphicsOpts & { filename: string } {
  const p = paramMap(params);
  const str = (k: string) => p.get(k) || undefined;
  return {
    filename: p.get('filename') ?? '', lyxscale: str('lyxscale'), draft: p.has('draft'), scale: str('scale'), width: str('width'), height: str('height'),
    keepAspectRatio: p.has('keepAspectRatio'), rotateAngle: str('rotateAngle'), rotateOrigin: str('rotateOrigin'), scaleBeforeRotation: p.has('scaleBeforeRotation'),
    BoundingBox: str('BoundingBox'), clip: p.has('clip'), special: str('special'), groupId: str('groupId'),
  };
}

/** Insert ▸ Graphics: a graphics inset at the cursor — on a layout page outside its text boxes, an image object on that page. */
export function insertGraphics(filename: string, opts: GraphicsOpts = {}): Command {
  const inline = insertNode(schema.nodes.graphics.create({ params: JSON.stringify(graphicsParams(filename, opts)) }));
  return (state, dispatch, view) => {
    if (isLayoutDoc(state.doc) && !state.selection.$from.parent.inlineContent) {
      if (!view) return false;
      if (dispatch) void placeImage(view, filename, pageAt(state.doc, state.selection.from)?.pos);
      return true;
    }
    return inline(state, dispatch, view);
  };
}

export function insertCommand(cmd: string, params: string[]): Command {
  return insertNode(schema.nodes.command.create({ cmd, params: JSON.stringify([...params, '']) }));
}
export const insertLabel = (name: string) => insertCommand('label', ['LatexCommand label', `name "${name}"`]);

/** Update all cross-references (ref/eqref/pageref/…) that point at `oldName` to `newName`; returns the count. */
export function renameLabelRefs(view: EditorView, oldName: string, newName: string): number {
  if (!oldName || oldName === newName) return 0;
  const tr = view.state.tr;
  let n = 0;
  view.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'command' || node.attrs.cmd !== 'ref') return true;
    const lines: string[] = (() => { try { return JSON.parse(node.attrs.params || '[]'); } catch { return []; } })();
    const i = lines.findIndex(l => l.replace(/^\t/, '').startsWith('reference '));
    if (i < 0) return true;
    const targets = unquote(lines[i].slice(lines[i].indexOf('reference ') + 10)).split(',').map(t => t.trim());
    if (!targets.includes(oldName)) return true;
    const updated = targets.map(t => (t === oldName ? newName : t)).join(',');
    const next = [...lines];
    next[i] = `reference "${updated}"`;
    tr.setNodeMarkup(tr.mapping.map(pos), undefined, { ...node.attrs, params: JSON.stringify(next) });
    n++;
    return true;
  });
  if (n) view.dispatch(tr);
  return n;
}

/** Count the cross-references pointing at a label name in a view's document. */
export function countLabelRefs(view: EditorView, name: string): number {
  if (!name) return 0;
  let n = 0;
  view.state.doc.descendants(node => {
    if (node.type.name === 'command' && node.attrs.cmd === 'ref') {
      const lines: string[] = (() => { try { return JSON.parse(node.attrs.params || '[]'); } catch { return []; } })();
      const line = lines.find(l => l.replace(/^\t/, '').startsWith('reference '));
      if (line && unquote(line.slice(line.indexOf('reference ') + 10)).split(',').map(t => t.trim()).includes(name)) n++;
    }
    return true;
  });
  return n;
}

/** Set the `name` of a label command inset (used by the Label dialog when editing a figure/table label). */
export function setLabelName(view: EditorView, pos: number, newName: string): void {
  const node = view.state.doc.nodeAt(pos);
  if (!node || node.type.name !== 'command') return;
  const lines: string[] = (() => { try { return JSON.parse(node.attrs.params || '[]'); } catch { return []; } })();
  const i = lines.findIndex(l => l.replace(/^\t/, '').startsWith('name '));
  if (i >= 0) lines[i] = `name "${newName}"`; else lines.push(`name "${newName}"`);
  view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, params: JSON.stringify(lines) }));
}

/** Delete a label command inset at `pos`. */
export function deleteLabelAt(view: EditorView, pos: number): void {
  const node = view.state.doc.nodeAt(pos);
  if (node) view.dispatch(view.state.tr.delete(pos, pos + node.nodeSize));
}

export const insertRef = (name: string, kind = 'ref') => insertCommand('ref', [`LatexCommand ${kind}`, `reference "${name}"`, 'plural "false"', 'caps "false"', 'noprefix "false"', 'nolink "false"']);
export const insertCite = (keys: string[], cmd = 'cite', before = '', after = '') => {
  const p = [`LatexCommand ${cmd}`];
  if (after) p.push(`after "${after}"`);
  if (before) p.push(`before "${before}"`);
  p.push(`key "${keys.join(',')}"`, 'literal "false"');
  return insertCommand('citation', p);
};
export const insertHref = (target: string, name = '') => insertCommand('href', ['LatexCommand href', `name "${name}"`, `target "${target}"`, 'literal "false"']);
export const insertInclude = (filename: string, kind = 'include') => insertCommand('include', [`LatexCommand ${kind}`, `filename "${filename}"`, 'literal "true"']);
/**
 * Make sure the document's BibTeX inset lists a bibliography file (e.g. `cited` for cited.bib):
 * 'added' when it was appended to the inset's list, 'present' when it was there already, 'none'
 * when this document has no BibTeX inset (a child document's bibliography lives in the master).
 */
export function ensureBibFile(view: EditorView, name: string): 'added' | 'present' | 'none' {
  let result: 'added' | 'present' | 'none' = 'none';
  const want = name.replace(/\.bib$/, '');
  view.state.doc.descendants((node, pos) => {
    if (result !== 'none' || node.type.name !== 'command') return true;
    const params: string[] = JSON.parse(node.attrs.params || '[]');
    if (!params.includes('LatexCommand bibtex')) return true;
    const i = params.findIndex(p => p.startsWith('bibfiles '));
    const files = i >= 0 ? params[i].replace(/^bibfiles\s+/, '').replace(/^"|"$/g, '').split(',').map(s => s.trim()).filter(Boolean) : [];
    if (files.some(f => f.replace(/\.bib$/, '') === want)) { result = 'present'; return false; }
    const next = [...params];
    const line = `bibfiles "${[...files, want].join(',')}"`;
    if (i >= 0) next[i] = line; else next.splice(1, 0, line);
    view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, params: JSON.stringify(next) }));
    result = 'added';
    return false;
  });
  return result;
}

export const insertBibtex = (files: string, style = 'plain') => insertCommand('bibtex', ['LatexCommand bibtex', 'btprint "btPrintCited"', `bibfiles "${files}"`, `options "${style}"`, 'encoding "default"']);
export const insertToc = (kind = 'tableofcontents') => insertCommand('toc', [`LatexCommand ${kind}`]);
export const insertIndexPrint = insertCommand('index_print', ['LatexCommand printindex', 'type "idx"', 'name "Index"', 'literal "false"']);

export const insertQuote = (side: 'l' | 'r', style = 'e', level = 'd') => insertNode(schema.nodes.quotes.create({ kind: `${style}${side}${level}` }));
export const insertSpace = (kind: string) => insertNode(schema.nodes.space.create({ kind }));
export const insertNewline = (kind: 'newline' | 'linebreak' = 'newline') => insertNode(schema.nodes.newline.create({ kind }));
export const insertNewpage = (kind = 'newpage') => insertNode(schema.nodes.newpage.create({ kind }));
export const insertSpecial = (arg: string) => insertNode(schema.nodes.special.create({ token: '\\SpecialChar', arg }));
export const insertHyphens = (token: '\\twohyphens' | '\\threehyphens') => insertNode(schema.nodes.special.create({ token, arg: '' }));
/**
 * Type an em dash (—) or en dash (–) as the character itself — what a keyboard that has the key
 * produces, and what the parser makes of `---` / `--` in the file (the writer turns it back into the
 * ligature). Alt+- / Alt+Shift+- (keymap.ts) and Insert ▸ Special Character.
 */
export const insertDash = (kind: 'em' | 'en'): Command => (state, dispatch) => {
  dispatch?.(state.tr.insertText(kind === 'em' ? '\u2014' : '\u2013').scrollIntoView());
  return true;
};
export const insertVSpace = (kind = 'defskip') => insertNode(schema.nodes.leaf.create({ name: 'VSpace', arg: kind, params: '[]' }));
export const insertMacroDef = (name: string, args = 0, def = '') => insertNode(schema.nodes.macro.create({ lines: JSON.stringify([`\\newcommand{\\${name}}${args ? `[${args}]` : ''}{${def}}`]) }));

/** Smart quotes: typing " inserts a LyX Quotes inset (opening/closing by context). */
export const smartQuote: Command = (state, dispatch) => {
  const $from = state.selection.$from;
  const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 1), $from.parentOffset, undefined, ' ');
  const nodeBefore = $from.nodeBefore;
  const opening = $from.parentOffset === 0 || /[\s(\[{]/.test(before) || (nodeBefore && !nodeBefore.isText && nodeBefore.type.name !== 'quotes');
  const style = (editorContext.meta?.language ?? 'english').startsWith('german') ? 'g' : (editorContext.meta?.language ?? '').startsWith('french') ? 'f' : 'e';
  return insertQuote(opening ? 'l' : 'r', style)(state, dispatch);
};

/* ------------------------------------------------------------------ math */

export function insertMath(display: boolean, env?: string, opts: { texSpaces?: boolean } = {}): (view: EditorView) => boolean {
  return (view) => {
    const { state } = view;
    let node: PMNode;
    const attrs: Record<string, unknown> = {};
    // the formula takes the font at the cursor (LyX), plus the change mark while tracking
    const markList: { type: string; attrs: Attrs }[] = inheritedFontMarks(state);
    if (editorContext.trackChanges && editorContext.changeAuthorId !== undefined) markList.push({ type: 'change', attrs: { type: 'inserted', author: editorContext.changeAuthorId, time: Math.floor(Date.now() / 1000) } });
    if (markList.length) attrs.marks = JSON.stringify(markList);
    // selected text becomes the formula content
    const sel = state.selection;
    const selText = sel.empty || sel instanceof NodeSelection || sel instanceof CellSelection ? '' : state.doc.textBetween(sel.from, sel.to, ' ');
    if (display) {
      const e = env ?? 'simple';
      const latex = e === 'simple' ? `\\[\n${selText}\n\\]` : `\\begin{${e}}\n${selText}\n\\end{${e}}`;
      node = schema.nodes.math_display.create({ ...attrs, latex });
    } else {
      node = schema.nodes.math_inline.create({ ...attrs, latex: selText, delim: '$' });
    }
    if (outsideLayoutText(state, true)) return false;
    // a selected object stays (keepSelectedObjects): the formula goes after it
    const tr = keepSelectedObjects(state.tr);
    const pos = tr.selection.from;   // the node is inserted where the selection starts
    pendingFocus.pos = pos; pendingFocus.keys = []; pendingFocus.texSpaces = !!opts.texSpaces;
    tr.replaceSelectionWith(node, false);
    view.dispatch(tr);
    // focus the mathfield (the node view DOM exists right after dispatch; retry on the next frame)
    const focusField = (): boolean => {
      const nv = (view as any).nodeDOM(pos) as HTMLElement | null;
      const spec = (nv as any)?.pmViewDesc?.spec;
      if (!spec?.focus) return false;
      spec.focus('end');
      return true;
    };
    if (!focusField()) requestAnimationFrame(() => focusField());
    return true;
  };
}

import { inRawText } from './rawtext';

/**
 * A formula from the toolbar or a menu: Space in it is a TeX space (as in one opened with `$`), not
 * LyX's "leave the formula" — "E = mc^2" typed after the Σ button kept only "E" as math and wrote the
 * rest as text (usability wave 3, a newcomer). Ctrl+M, LyX's key, keeps LyX's Space.
 */
export const insertMathFromUi = (display: boolean, env?: string) => insertMath(display, env, { texSpaces: true });

export { inRawText };

/**
 * `$` typed in the text opens an inline formula (a second `$` right away makes it a display
 * formula, `$` inside closes it, Backspace in the empty formula gives the typed dollars back as
 * text — see LyxMathField.dollar). In raw LaTeX the dollar stays a character.
 */
export const typeDollar: Command = (state, _dispatch, view) => {
  if (!view || !state.selection.$from.parent.isTextblock || inRawText(state)) return false;
  pendingFocus.dollar = '$';
  return insertMath(false)(view);
};

/** Toggle inline <-> display for the math node at/near the selection. */
export const toggleMathDisplay: Command = (state, dispatch, view) => {
  const sel = state.selection;
  let pos = -1, node: PMNode | null = null;
  // the formula being edited, wherever the document's own caret was left: the Edit menu turned the
  // display equation beside that caret into inline math instead (and its \label with it)
  const field = editorContext.mathField;
  if (view && field?.dom.isConnected) {
    state.doc.descendants((n, p) => {
      if (node) return false;
      if ((n.type.name === 'math_inline' || n.type.name === 'math_display') && view.nodeDOM(p)?.contains(field.dom)) { pos = p; node = n; }
      return !node;
    });
  }
  if (node) { /* found */ }
  else if (sel instanceof NodeSelection && (sel.node.type.name === 'math_inline' || sel.node.type.name === 'math_display')) { pos = sel.from; node = sel.node; }
  else {
    const nb = sel.$from.nodeBefore, na = sel.$from.nodeAfter;
    if (nb && (nb.type.name === 'math_inline' || nb.type.name === 'math_display')) { pos = sel.from - nb.nodeSize; node = nb; }
    else if (na && (na.type.name === 'math_inline' || na.type.name === 'math_display')) { pos = sel.from; node = na; }
  }
  if (!node) return false;
  if (!dispatch) return true;
  const target: PMNode = node;
  let repl: PMNode;
  let lostLabels: string[] = [];
  if (target.type.name === 'math_inline') repl = schema.nodes.math_display.create({ marks: target.attrs.marks, latex: `\\[\n${target.attrs.latex}\n\\]` });
  else {
    const m = /\\\[\s*([\s\S]*?)\s*\\\]/.exec(target.attrs.latex) ?? /\\begin\{[a-z*]+\}\s*([\s\S]*?)\s*\\end\{[a-z*]+\}/.exec(target.attrs.latex);
    lostLabels = [...String(target.attrs.latex).matchAll(/\\label\{([^}]*)\}/g)].map(x => x[1]);
    repl = schema.nodes.math_inline.create({ marks: target.attrs.marks, latex: (m?.[1] ?? target.attrs.latex).replace(/\\label\{[^}]*\}/g, '').trim(), delim: '$' });
  }
  dispatch(state.tr.replaceWith(pos, pos + target.nodeSize, repl));
  // an inline formula has no number: references to the equation now print ?? — say so
  if (lostLabels.length) editorContext.notify?.(`The equation is inline now and has no number: its label ${lostLabels.join(', ')} is gone, references to it print ?? — Ctrl+Z brings it back`);
  return true;
};

/* --------------------------------------------------------------- navigation */

/** Move the cursor into an adjacent math field when arrow keys reach it. */
export function arrowIntoMath(dir: -1 | 1): (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined, view?: EditorView) => boolean {
  return (state, _dispatch, view) => {
    if (!view) return false;
    const sel = state.selection;
    if (!(sel instanceof TextSelection) || !sel.empty) return false;
    const $c = sel.$cursor!;
    const node = dir > 0 ? $c.nodeAfter : $c.nodeBefore;
    if (!node || (node.type.name !== 'math_inline' && node.type.name !== 'math_display' && node.type.name !== 'macro')) return false;
    const pos = dir > 0 ? $c.pos : $c.pos - node.nodeSize;
    const dom = view.nodeDOM(pos) as HTMLElement | null;
    const spec = (dom as any)?.pmViewDesc?.spec;
    if (!spec?.focus) return false;
    spec.focus(dir > 0 ? 'start' : 'end');
    return true;
  };
}

/** Select the whole inset around the cursor (Ctrl+A in LyX: inset-select-all). */
export const selectInset: Command = (state, dispatch) => {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'inset' || n.type.name === 'table_cell') {
      const start = $from.start(d), end = $from.end(d);
      if (state.selection.from === start && state.selection.to === end) continue;
      dispatch?.(state.tr.setSelection(TextSelection.create(state.doc, start, end)));
      return true;
    }
  }
  dispatch?.(state.tr.setSelection(TextSelection.create(state.doc, 0, state.doc.content.size)));
  return true;
};

/** Toggle open/collapsed on the inset around the cursor (Ctrl+I: inset-toggle). */
export const toggleInset: Command = (state, dispatch) => {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'inset') {
      const pos = $from.before(d);
      const status = n.attrs.status === 'collapsed' ? 'open' : 'collapsed';
      let tr = state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, status });
      if (status === 'collapsed') tr = tr.setSelection(TextSelection.near(tr.doc.resolve(pos + n.nodeSize)));
      dispatch?.(tr);
      return true;
    }
  }
  // node selection on an inset?
  const sel = state.selection;
  if (sel instanceof NodeSelection && sel.node.type.name === 'inset') {
    dispatch?.(state.tr.setNodeMarkup(sel.from, undefined, { ...sel.node.attrs, status: sel.node.attrs.status === 'collapsed' ? 'open' : 'collapsed' }));
    return true;
  }
  return false;
};

export function setAllInsets(status: 'open' | 'collapsed'): Command {
  return (state, dispatch) => {
    const tr = state.tr;
    state.doc.descendants((node, pos) => {
      if (node.type.name === 'inset' && node.attrs.status && node.attrs.status !== status) tr.setNodeMarkup(pos, undefined, { ...node.attrs, status });
      return true;
    });
    if (!tr.docChanged) return false;
    dispatch?.(tr);
    return true;
  };
}

/** Move the paragraph up/down (Alt+Up/Down: paragraph-move-up/down). */
export function moveParagraph(dir: -1 | 1): Command {
  return (state, dispatch) => {
    const cur = currentParagraph(state);
    if (!cur) return false;
    const $pos = state.doc.resolve(cur.pos);
    const parent = $pos.parent;
    const index = $pos.index();
    const target = index + dir;
    if (target < 0 || target >= parent.childCount) return false;
    const other = parent.child(target);
    const otherPos = dir < 0 ? cur.pos - other.nodeSize : cur.pos + cur.node.nodeSize;
    if (!dispatch) return true;
    const offset = state.selection.from - cur.pos;
    let tr = state.tr;
    if (dir < 0) {
      tr = tr.delete(cur.pos, cur.pos + cur.node.nodeSize).insert(otherPos, cur.node);
      tr = tr.setSelection(TextSelection.create(tr.doc, otherPos + offset));
    } else {
      tr = tr.delete(cur.pos, cur.pos + cur.node.nodeSize).insert(cur.pos + other.nodeSize, cur.node);
      tr = tr.setSelection(TextSelection.create(tr.doc, cur.pos + other.nodeSize + offset));
    }
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/* ------------------------------------------------------------------ tables */

/** A rows × cols table at the cursor; selected text (or object, or paragraphs) moves into its first cell (LyX). */
export function insertTable(rows: number, cols: number): Command {
  return (state, dispatch) => {
    if (outsideLayoutText(state, dispatch)) return false;
    const taken = selectedParagraphs(state);
    const cell = (r: number, c: number) => {
      const attrs: [string, string][] = [['alignment', 'center'], ['valignment', 'top']];
      if (r === 0) attrs.push(['topline', 'true']);
      attrs.push(['bottomline', 'true']);
      if (c === 0) attrs.push(['leftline', 'true']);
      attrs.push(['rightline', 'true'], ['usebox', 'none']);
      return schema.nodes.table_cell.create({ attrs: JSON.stringify(attrs) }, r === 0 && c === 0 && taken ? taken : schema.nodes.paragraph.create({ layout: 'Plain Layout' }));
    };
    const rowsN: PMNode[] = [];
    for (let r = 0; r < rows; r++) {
      const cells: PMNode[] = [];
      for (let c = 0; c < cols; c++) cells.push(cell(r, c));
      rowsN.push(schema.nodes.table_row.create({ attrs: '[]' }, Fragment.from(cells)));
    }
    const columns = Array.from({ length: cols }, () => [['alignment', 'center'], ['valignment', 'top']]);
    const table = schema.nodes.table.create({ attrs: JSON.stringify([['version', '3'], ['rows', String(rows)], ['columns', String(cols)]]), features: JSON.stringify([['tabularvalignment', 'middle']]), columns: JSON.stringify(columns) }, Fragment.from(rowsN));
    if (!dispatch) return true;
    let tr = state.tr;
    if (!taken && !state.selection.empty) tr.setSelection(Selection.near(tr.doc.resolve(state.selection.to), -1));
    tr = tr.replaceSelectionWith(table, false);
    const pos = tr.selection.from - table.nodeSize;
    tr = tr.setSelection(TextSelection.create(tr.doc, pos + 4));
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** Set a LyX cell attribute (e.g. topline true, alignment left) on selected cells. */
/** The table, row and cell around the cursor with their indices. */
export interface TableContext {
  table: PMNode; tablePos: number; row: PMNode; rowPos: number; cell: PMNode; cellPos: number;
  rowIndex: number; colIndex: number; nrows: number; ncols: number;
}
export function tableContext(state: EditorState): TableContext | null {
  const $from = state.selection.$from;
  let cell: { node: PMNode; pos: number } | null = null, row: { node: PMNode; pos: number } | null = null, table: { node: PMNode; pos: number } | null = null;
  for (let d = $from.depth; d >= 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'table_cell' && !cell) cell = { node: n, pos: $from.before(d) };
    else if (n.type.name === 'table_row' && !row) row = { node: n, pos: $from.before(d) };
    else if (n.type.name === 'table' && !table) { table = { node: n, pos: $from.before(d) }; break; }
  }
  if (!cell || !row || !table) return null;
  let colIndex = 0, rowIndex = 0, ncols = 0;
  row.node.forEach((c, offset) => { if (row!.pos + 1 + offset < cell!.pos) colIndex += c.attrs.colspan ?? 1; });
  table.node.forEach((r, offset, i) => { if (table!.pos + 1 + offset === row!.pos) rowIndex = i; let n = 0; r.forEach(c => { n += c.attrs.colspan ?? 1; }); ncols = Math.max(ncols, n); });
  return { table: table.node, tablePos: table.pos, row: row.node, rowPos: row.pos, cell: cell.node, cellPos: cell.pos, rowIndex, colIndex, nrows: table.node.childCount, ncols };
}

function withAttr(attrs: [string, string][], key: string, value: string | null): [string, string][] {
  const out = attrs.map(a => [a[0], a[1]] as [string, string]);
  const i = out.findIndex(a => a[0] === key);
  if (value === null) { if (i >= 0) out.splice(i, 1); }
  else if (i >= 0) out[i] = [key, value];
  else { const ub = out.findIndex(a => a[0] === 'usebox'); out.splice(ub >= 0 ? ub : out.length, 0, [key, value]); }
  return out;
}
const jsonAttrs = (s: string): [string, string][] => { try { return JSON.parse(s || '[]'); } catch { return []; } };

export interface TableChanges { cell?: [string, string | null][]; column?: [string, string | null][]; row?: [string, string | null][]; table?: [string, string | null][] }

/** Apply the table-settings dialog in one transaction: cell attrs, column attrs (+ alignment of the
 *  column's cells, as LyX does), row attrs (lines go to the row's cells) and table features. */
export function setTableAttrs(ch: TableChanges): Command {
  return (state, dispatch) => {
    const ctx = tableContext(state);
    if (!ctx) return false;
    const tr = state.tr;
    const setCell = (pos: number, node: PMNode, kv: [string, string | null][]) => {
      let attrs = jsonAttrs(node.attrs.attrs);
      for (const [k, v] of kv) attrs = withAttr(attrs, k, v);
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, attrs: JSON.stringify(attrs) });
    };
    if (ch.cell?.length) setCell(ctx.cellPos, ctx.cell, ch.cell);
    if (ch.row?.length) {
      const lines = ch.row.filter(([k]) => /line$/.test(k));
      const rest = ch.row.filter(([k]) => !/line$/.test(k));
      if (lines.length) ctx.row.forEach((c, offset) => { const pos = ctx.rowPos + 1 + offset; setCell(pos, pos === ctx.cellPos && ch.cell?.length ? tr.doc.nodeAt(pos)! : c, lines); });
      if (rest.length) { let attrs = jsonAttrs(ctx.row.attrs.attrs); for (const [k, v] of rest) attrs = withAttr(attrs, k, v); tr.setNodeMarkup(ctx.rowPos, undefined, { ...ctx.row.attrs, attrs: JSON.stringify(attrs) }); }
    }
    if (ch.column?.length || ch.table?.length) {
      const columns: [string, string][][] = (() => { try { return JSON.parse(ctx.table.attrs.columns || '[]'); } catch { return []; } })();
      while (columns.length < ctx.ncols) columns.push([['alignment', 'center'], ['valignment', 'top']]);
      if (ch.column?.length) {
        columns[ctx.colIndex] = ch.column.reduce((a, [k, v]) => withAttr(a, k, v), columns[ctx.colIndex]);
        const align = ch.column.filter(([k]) => k === 'alignment' || k === 'valignment');
        if (align.length) ctx.table.forEach((r, roff) => {
          let ci = 0;
          r.forEach((c, coff) => { const pos = ctx.tablePos + 1 + roff + 1 + coff; if (ci === ctx.colIndex) setCell(pos, tr.doc.nodeAt(pos)!, align); ci += c.attrs.colspan ?? 1; });
        });
      }
      let features = jsonAttrs(ctx.table.attrs.features);
      for (const [k, v] of ch.table ?? []) features = withAttr(features, k, v);
      tr.setNodeMarkup(ctx.tablePos, undefined, { ...ctx.table.attrs, columns: JSON.stringify(columns), features: JSON.stringify(features) });
    }
    if (dispatch) dispatch(tr);
    return true;
  };
}

export function setCellAttr(key: string, value: string | null): Command {
  return (state, dispatch) => {
    const { from, to } = state.selection;
    const tr = state.tr;
    let any = false;
    const apply = (node: PMNode, pos: number) => {
      const attrs: [string, string][] = JSON.parse(node.attrs.attrs || '[]');
      const idx = attrs.findIndex(a => a[0] === key);
      if (value === null) { if (idx >= 0) attrs.splice(idx, 1); }
      else if (idx >= 0) attrs[idx] = [key, value];
      else {
        // keep LyX's attribute ordering roughly: insert before usebox
        const ub = attrs.findIndex(a => a[0] === 'usebox');
        attrs.splice(ub >= 0 ? ub : attrs.length, 0, [key, value]);
      }
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, attrs: JSON.stringify(attrs) });
      any = true;
    };
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === 'table_cell') { apply(node, pos); return false; }
      return true;
    });
    if (!any) {
      // cursor inside a cell
      const $from = state.selection.$from;
      for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === 'table_cell') { apply($from.node(d), $from.before(d)); break; }
    }
    if (!any) return false;
    dispatch?.(tr);
    return true;
  };
}

export function findMathView(view: EditorView, pos: number): MathInlineView | MathDisplayView | null {
  const dom = view.nodeDOM(pos) as any;
  return dom?.pmViewDesc?.spec ?? null;
}

export function selectionParentInset(state: EditorState): { node: PMNode; pos: number } | null {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'inset') return { node: n, pos: $from.before(d) };
  }
  const sel = state.selection;
  if (sel instanceof NodeSelection && sel.node.type.name === 'inset') return { node: sel.node, pos: sel.from };
  return null;
}

export function nearestNode(state: EditorState, types: string[]): { node: PMNode; pos: number } | null {
  const sel = state.selection;
  if (sel instanceof NodeSelection && types.includes(sel.node.type.name)) return { node: sel.node, pos: sel.from };
  const $from = sel.$from;
  const nb = $from.nodeBefore, na = $from.nodeAfter;
  if (na && types.includes(na.type.name)) return { node: na, pos: $from.pos };
  if (nb && types.includes(nb.type.name)) return { node: nb, pos: $from.pos - nb.nodeSize };
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (types.includes(n.type.name)) return { node: n, pos: $from.before(d) };
  }
  return null;
}

export { splitBlock, Selection, type ResolvedPos };

/** LyX "dissolve inset": replace an inset by its content (paragraph contents joined inline). */
export function dissolveInset(pos: number): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (!node || node.type.name !== 'inset') return false;
    if (!dispatch) return true;
    const content: PMNode[] = [];
    node.forEach((para, _o, i) => {
      if (i > 0 && content.length) content.push(state.schema.text(' '));
      para.forEach(c => content.push(c));
    });
    const tr = state.tr.replaceWith(pos, pos + node.nodeSize, content);
    dispatch(tr.setSelection(TextSelection.near(tr.doc.resolve(pos))));
    if (node.attrs.name === 'Note') recordInteraction('note-dissolve', 'removed', {
      target: node.attrs.arg === 'Comment' ? 'comment' : node.attrs.arg === 'Greyedout' ? 'greyedout' : 'note',
      content: node.content.size <= 2 ? 'empty' : 'filled', display: node.attrs.status === 'collapsed' ? 'collapsed' : 'inline', tracking: editorContext.trackChanges ? 'on' : 'off',
    });
    return true;
  };
}

/** Project-relative id of the child document referenced by an include command node. */
export function includeTarget(node: PMNode, project: string, docDir: string): string | null {
  if (node.type.name !== 'command' || node.attrs.cmd !== 'include') return null;
  let fn = '';
  try { fn = unquote(paramMap(JSON.parse(node.attrs.params || '[]')).get('filename')); } catch { return null; }
  if (!fn) return null;
  const parts = [...docDir.split('/').filter(Boolean), ...fn.split('/')];
  const out: string[] = [];
  for (const p of parts) { if (p === '..') out.pop(); else if (p !== '.' && p !== '') out.push(p); }
  return project + '/' + out.join('/');
}
